
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from "react";
import { Filter, ShoppingCart, Loader2, LayoutGrid, List, Wallet, ShieldCheck } from "lucide-react";
import { useProducts } from "../../hooks/useProducts";
import { useCart } from "../../context/CartContext";
import { useWallet } from "../../hooks/useWallet";
import { Button } from "../ui/Button";
import { Product } from "../../types/products";

const ProductListItem: React.FC<{ item: Product; onAddToCart: (item: Product) => void }> = ({ item, onAddToCart }) => (
    <div className="bg-white dark:bg-[#15171C] rounded-xl border border-stone-200 dark:border-white/5 p-4 flex flex-col sm:flex-row items-center gap-4 group hover:shadow-lg transition-shadow duration-300 text-center sm:text-left">
        <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gray-50 dark:bg-[#0A0B0F] rounded-lg flex items-center justify-center p-2 shrink-0">
           <img src={item.image} alt={item.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="flex-1">
           <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">{item.category}</div>
           <h3 className="font-serif text-lg text-stone-900 dark:text-white leading-tight mb-2">{item.name}</h3>
           <p className="text-xs text-stone-500 mb-4 line-clamp-2">{item.description}</p>
        </div>
        <div className="flex sm:flex-col items-center justify-between sm:justify-center w-full sm:w-auto gap-4 sm:gap-2 sm:pl-4 sm:border-l border-stone-100 dark:border-white/5">
          <div className="text-xl font-bold text-nobel-gold font-serif">${item.price.toFixed(2)}</div>
          <Button onClick={() => onAddToCart(item)} size="sm" variant="secondary" icon={<ShoppingCart size={14}/>}>
             Add to Cart
          </Button>
        </div>
    </div>
);

export const MarketplaceView: React.FC = () => {
  const { products, isLoading } = useProducts();
  const { addToCart } = useCart();
  const { isConnected, walletAddress } = useWallet();
  const [view, setView] = useState<'grid' | 'list'>('grid');

  return (
    <div className="space-y-6 animate-fade-in">
       <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h2 className="font-serif text-4xl text-stone-900 dark:text-white font-bold tracking-tight">Marketplace</h2>
            <p className="text-stone-500 text-sm">Curated tools and services for your sovereign business.</p>
          </div>
          <div className="flex items-center gap-4">
              {isConnected ? (
                  <div className="hidden lg:flex items-center gap-3 px-4 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                      <ShieldCheck size={14} /> Identity Linked: {walletAddress?.slice(0, 6)}...
                  </div>
              ) : (
                  <div className="hidden lg:flex items-center gap-3 px-4 py-2 bg-amber-500/5 border border-amber-500/20 rounded-lg text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest">
                      <Wallet size={14} /> Connect to Transact
                  </div>
              )}
              <div className="flex items-center gap-1 p-1 rounded-lg bg-stone-100 dark:bg-white/10">
                 <button onClick={() => setView('grid')} className={`p-2 rounded-md transition-colors ${view === 'grid' ? 'bg-white dark:bg-black/20 shadow-sm' : 'text-stone-500 hover:bg-white/50 dark:hover:bg-black/10'}`}>
                    <LayoutGrid size={18} />
                 </button>
                 <button onClick={() => setView('list')} className={`p-2 rounded-md transition-colors ${view === 'list' ? 'bg-white dark:bg-black/20 shadow-sm' : 'text-stone-500 hover:bg-white/50 dark:hover:bg-black/10'}`}>
                    <List size={18} />
                 </button>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-white/10 border border-stone-200 dark:border-white/10 rounded-lg text-stone-600 dark:text-white hover:border-nobel-gold transition-colors text-xs font-bold uppercase tracking-wider">
                 <Filter size={16} /> Filters
              </button>
          </div>
       </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-96">
            <Loader2 className="animate-spin text-stone-300 dark:text-stone-700" size={32} />
        </div>
      ) : (
        view === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(item => (
                    <div key={item.id} className="bg-white dark:bg-[#15171C] rounded-xl border border-stone-200 dark:border-white/5 overflow-hidden group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full">
                        <div className="h-48 overflow-hidden relative bg-gray-50 dark:bg-[#0A0B0F] flex items-center justify-center p-4">
                        <img src={item.image} alt={item.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wide text-stone-800 shadow-sm">
                            {item.category}
                        </div>
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-serif text-lg text-stone-900 dark:text-white leading-tight font-bold">{item.name}</h3>
                        </div>
                        <p className="text-xs text-stone-500 mb-4 line-clamp-2 flex-grow">{item.description}</p>
                        <div className="mt-auto flex items-end justify-between pt-4 border-t border-stone-50 dark:border-white/5">
                            <div className="text-xl font-bold text-nobel-gold font-serif">${item.price.toFixed(2)}</div>
                            <Button onClick={() => addToCart(item)} size="sm" variant="secondary" icon={<ShoppingCart size={14}/>}>
                                Add
                            </Button>
                        </div>
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="space-y-4">
                {products.map(item => (
                    <ProductListItem key={item.id} item={item} onAddToCart={addToCart} />
                ))}
            </div>
        )
      )}
    </div>
  );
};
